import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    BlockStack,
    Box,
    Button,
    Card,
    Combobox,
    Form,
    FormLayout,
    IndexFilters,
    IndexTable,
    InlineStack,
    Layout,
    Listbox,
    Page,
    Pagination,
    SkeletonThumbnail,
    Tag,
    Text,
    TextField,
    Thumbnail,
    useBreakpoints,
    useSetIndexFiltersMode,
} from '@shopify/polaris';
import { useNavigate } from 'react-router';

// Contexts and utilities
import { SessionContext } from '#client/contexts/SessionContext';
import { useToast } from '#client/components/common/Toast/ToastContext';
import MultiProductFilter from '#client/components/features/MultiProductFilter/MultiProductFilter';
import { useProductFilterActions, useProductFilterStore, useCycleCountActions } from '#client/stores/cycleCount/useCycleCountStore';
import { multiProductFilterService } from '#client/services/multiProductFilter/multiProductFilterServices';
import { ITEMS_PER_PAGE, TYPE_OPTIONS } from '#client/pages/CycleCounts/constants/cycleCount';
import { cycleCountService } from '#client/services/cycleCount/cycleCountService';
import { useReasonCodes } from '#client/hooks/useReasonCodes';
import { hasPermission } from '#client/utils/helpers';
import { useQueryClient } from '@tanstack/react-query';

const CreateCycleCounts = () => {
    const navigate = useNavigate();
    const { addToast } = useToast();
    const { currentUser } = useContext(SessionContext);
    const queryClient = useQueryClient();

    // Form state
    const [selectedStoreIds, setSelectedStoreIds] = useState([]);
    const [storeSearchValue, setStoreSearchValue] = useState('');
    const [countType, setCountType] = useState('');
    const [reasonCode, setReasonCode] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    // Product selection state (for Partial counts) - using MultiProductFilter
    const restrictToEligibleProducts = useProductFilterStore.use.restrictToEligibleProducts();
    const allProducts = useProductFilterStore.use.allProducts();
    const filters = useProductFilterStore.use.filters();
    const eligibleProductRules = useProductFilterStore.use.eligibleProductRules();
    const conditionType = useProductFilterStore.use.conditionType();
    const snapshotId = useProductFilterStore.use.snapshotId();
    const totalProductCount = useProductFilterStore.use.totalProductCount();
    const currentPage = useProductFilterStore.use.currentPage();
    const { setAllProducts, setCurrentPage } = useProductFilterActions();
    const { resetCycleCount } = useCycleCountActions();

    useEffect(() => {
        // Reset form on mount
        resetCycleCount();
    }, [resetCycleCount]);

    // Pagination constants
    const itemsPerPage = ITEMS_PER_PAGE;

    // Product list filter state
    const [productNameFilter, setProductNameFilter] = useState('');
    const [queryValueFilter, setQueryValueFilter] = useState('');
    const [skuFilter, setSkuFilter] = useState('');
    const [selected, setSelected] = useState(0);

    // Breakpoints for responsive design
    const breakpoints = useBreakpoints();
    const { mode: filterMode, setMode: setFilterMode } = useSetIndexFiltersMode('FILTERING');

    // Has permission to create new cycle counts
    const canCreatePrescribe = hasPermission(currentUser, 'ccCreatePrescribe');
    const canCreateAdHoc = hasPermission(currentUser, 'ccCreateAdHoc');
    const canCreateCycleCount = canCreatePrescribe || canCreateAdHoc;

    // Invalidate cycle-counts cache after creating a new count
    const invalidateCycleCountsCache = useCallback(() => {
        queryClient.invalidateQueries(['cycle-counts']);
    }, []);

    // Count type options based on permissions
    const countTypeOptions = useMemo(() => {
        const options = TYPE_OPTIONS;

        // If user lacks ccCreatePrescribe permission, remove "Full" option
        if (!canCreatePrescribe) {
            return options.filter((option) => option.value !== 'full');
        }

        return options;
    }, [currentUser]);

    // Get available stores from currentUser.locations
    const availableStores = useMemo(() => {
        if (!currentUser?.locations || !currentUser?.locationNames) {
            return [];
        }

        const allStores = currentUser.locations.map((loc, index) => ({
            id: loc.locationId,
            name: currentUser.locationNames[index] || 'Unknown Location',
        }));

        // If activeStore is set, filter to only show that store
        if (currentUser.activeStore && currentUser.activeStore !== 'ALL' && currentUser.activeStore.length > 0) {
            return allStores.filter((store) => store.id === currentUser.activeStore);
        }

        return allStores;
    }, [currentUser]);

    // Filtered stores based on search
    const filteredStores = useMemo(() => {
        if (!storeSearchValue) return availableStores;
        const searchLower = storeSearchValue.toLowerCase();
        return availableStores.filter((store) => store.name.toLowerCase().includes(searchLower));
    }, [availableStores, storeSearchValue]);

    // Selected store objects for tags
    const selectedStores = useMemo(() => {
        return availableStores.filter((store) => selectedStoreIds.includes(store.id));
    }, [availableStores, selectedStoreIds]);

    // Store selection tags
    const storeVerticalContent = useMemo(() => {
        return selectedStores.length > 0 ? (
            <InlineStack gap="100">
                {selectedStores.map((store) => (
                    <Tag
                        key={store.id}
                        onRemove={() => {
                            setSelectedStoreIds((prev) => prev.filter((id) => id !== store.id));
                        }}
                    >
                        {store.name}
                    </Tag>
                ))}
            </InlineStack>
        ) : null;
    }, [selectedStores]);

    // Handle store selection
    const handleStoreSelect = useCallback(
        (value) => {
            if (value === 'ALL') {
                const allSelected = selectedStoreIds.length === availableStores.length;
                setSelectedStoreIds(allSelected ? [] : availableStores.map((s) => s.id));
            } else {
                const alreadySelected = selectedStoreIds.includes(value);
                setSelectedStoreIds(alreadySelected ? selectedStoreIds.filter((id) => id !== value) : [...selectedStoreIds, value]);
            }
            setStoreSearchValue('');
        },
        [selectedStoreIds, availableStores]
    );

    // Fetch reason codes using TanStack Query
    const { data: availableReasonCodes = [], isLoading: isLoadingReasonCodes, error: reasonCodesError } = useReasonCodes();

    // Filter reason codes based on count type
    const filteredReasonCodes = useMemo(() => {
        if (countType === 'full') {
            // For full counts, only show "Cycle Count" reason code
            return availableReasonCodes.filter((code) => code.value === 'cycle_count_available');
        }
        // For partial counts, show all available reason codes
        return availableReasonCodes;
    }, [countType, availableReasonCodes]);

    // Set default reason code based on count type
    useEffect(() => {
        if (!countType) {
            // Reset reason code if no count type selected
            setReasonCode('');
            return;
        }

        // For full counts, auto-select "Cycle Count"
        if (countType === 'full') {
            const cycleCountCode = availableReasonCodes.find((code) => code.value === 'cycle_count_available');
            if (cycleCountCode) {
                setReasonCode(cycleCountCode.id);
            }
        } else if (countType === 'partial' && !reasonCode && filteredReasonCodes.length > 0) {
            // For partial counts, keep user's selection or auto-select first available if none selected
            if (hasPermission(currentUser, 'ccCreatePrescribe')) {
                // Auto-select first reason code for partial counts
                setReasonCode(filteredReasonCodes[0].id);
            }
        }
    }, [countType, availableReasonCodes, filteredReasonCodes, reasonCode, currentUser]);

    // Show error toast if reason codes fail to load
    useEffect(() => {
        if (reasonCodesError) {
            addToast({
                type: 'error',
                message: 'Failed to load reason codes',
            });
        }
    }, [reasonCodesError, addToast]);

    // Individual field validation
    const hasStores = useMemo(() => selectedStoreIds.length > 0, [selectedStoreIds]);
    const hasCountType = useMemo(() => countType !== '', [countType]);
    const hasReasonCode = useMemo(() => reasonCode !== '', [reasonCode]);
    const hasDescription = useMemo(() => description.trim() !== '', [description]);
    const hasProducts = useMemo(() => countType === 'full' || (restrictToEligibleProducts && allProducts.length > 0), [countType, restrictToEligibleProducts, allProducts]);

    // Form validation - this will update on every keystroke
    const isFormValid = useMemo(() => {
        return hasStores && hasCountType && hasReasonCode && hasDescription && hasProducts;
    }, [hasStores, hasCountType, hasReasonCode, hasDescription, hasProducts]);

    // Handle form submission
    const handleSubmit = useCallback(
        async (event) => {
            event.preventDefault();

            if (!isFormValid) {
                setShowValidationErrors(true);
                addToast({
                    type: 'error',
                    message: (
                        <div>
                            Required fields missing <br />
                            {!hasProducts && (
                                <>
                                    * At least one product must be selected for Partial counts <br />
                                </>
                            )}
                            {!hasStores && (
                                <>
                                    * At least one store is required <br />
                                </>
                            )}
                            {!hasCountType && (
                                <>
                                    * Count type is required <br />
                                </>
                            )}
                            {!hasReasonCode && (
                                <>
                                    * Reason code is required <br />
                                </>
                            )}
                            {!hasDescription && (
                                <>
                                    * Description is required <br />
                                </>
                            )}
                        </div>
                    ),
                });
                return;
            }

            setIsSubmitting(true);

            try {
                const payload = {
                    locationIds: selectedStoreIds,
                    countType,
                    reasonCode,
                    description,
                    productRules: restrictToEligibleProducts
                        ? {
                              filters,
                              conditionType,
                              eligibleProductRules,
                          }
                        : null,
                };

                // For partial counts, use snapshot ID if available, otherwise fall back to products array
                if (countType === 'partial') {
                    if (snapshotId) {
                        payload.productSnapshotId = snapshotId;
                    } else {
                        payload.selectedProducts = allProducts;
                    }
                }

                const response = await cycleCountService.create(payload);

                let createdCycleCounts = [];
                if (response.success) {
                    createdCycleCounts = response.data?.cycleCounts;
                    const message = createdCycleCounts.length === 1 ? 'Cycle count created' : 'Cycle counts created';
                    addToast({
                        type: 'success',
                        message: `${createdCycleCounts.length} ${message}`,
                    });
                } else {
                    console.error('Cycle count creation response:', response);
                    addToast({
                        type: 'error',
                        message: 'Failed to create cycle count',
                    });
                    return false;
                }

                // Invalidate cycle-counts cache
                invalidateCycleCountsCache();

                // Navigate to the created cycle count(s) or list
                if (createdCycleCounts.length === 1) {
                    navigate(`/cycle-counts/${createdCycleCounts[0].id}`);
                } else {
                    navigate('/cycle-counts');
                }
            } catch (error) {
                addToast({
                    type: 'error',
                    message: error.response?.data?.error?.message || 'Failed to create cycle count',
                });
            } finally {
                setIsSubmitting(false);
            }
        },
        [isFormValid, countType, currentUser, selectedStoreIds, reasonCode, description, allProducts, addToast, navigate, setShowValidationErrors]
    );

    // Handle cancel
    const handleCancel = useCallback(() => {
        navigate('/cycle-counts');
    }, [navigate]);

    // Handle page navigation
    const handlePageNavigation = useCallback(
        async (newPage) => {
            if (!snapshotId) return;

            try {
                const response = await multiProductFilterService.getSnapshotPage(snapshotId, newPage, itemsPerPage);
                // Handle both product and variant responses
                const results = response?.data?.variants || response?.data?.products;
                if (results) {
                    setAllProducts(results);
                    setCurrentPage(newPage);
                }
            } catch (error) {
                addToast({
                    type: 'error',
                    message: error.response?.data?.error?.message || 'Failed to load products',
                });
            }
        },
        [snapshotId, setAllProducts, setCurrentPage, addToast]
    );

    // Render image helper
    const renderImage = useCallback((imageUrl, title) => {
        if (!imageUrl) {
            return <SkeletonThumbnail size="small" />;
        }
        return <Thumbnail source={imageUrl} alt={title || 'Product image'} size="small" />;
    }, []);

    // Product list filter handlers
    const handleProductNameChange = useCallback((value) => setProductNameFilter(value), []);
    const handleQueryValueChange = useCallback((value) => setQueryValueFilter(value), []);
    const handleProductNameRemove = useCallback(() => setProductNameFilter(''), []);
    const handleQueryValueRemove = useCallback(() => setQueryValueFilter(''), []);
    const handleSKUChange = useCallback((value) => setSkuFilter(value), []);
    const handleSKURemove = useCallback(() => setSkuFilter(''), []);

    const handleFiltersClearAll = useCallback(() => {
        setProductNameFilter('');
        setQueryValueFilter('');
        setSkuFilter('');
    }, []);

    const onHandleCancel = useCallback(() => {
        handleFiltersClearAll();
    }, [handleFiltersClearAll]);

    // Product list filters configuration
    const productFilters = useMemo(
        () => [
            {
                key: 'productName',
                label: 'Product Name',
                filter: <TextField label="Product Name" value={productNameFilter} onChange={handleProductNameChange} autoComplete="off" labelHidden />,
                shortcut: true,
            },
            {
                key: 'SKU',
                label: 'SKU',
                filter: <TextField label="SKU" value={skuFilter} onChange={handleSKUChange} autoComplete="off" labelHidden />,
                shortcut: true,
            },
        ],
        [productNameFilter, handleProductNameChange, skuFilter, handleSKUChange]
    );

    const appliedFilters = useMemo(() => {
        const filters = [];

        if (productNameFilter) {
            filters.push({
                key: 'productName',
                label: `Product name contains ${productNameFilter}`,
                onRemove: handleProductNameRemove,
            });
        }

        if (skuFilter) {
            filters.push({
                key: 'SKU',
                label: `SKU contains ${skuFilter}`,
                onRemove: handleSKURemove,
            });
        }

        return filters;
    }, [productNameFilter, handleProductNameRemove, skuFilter, handleSKURemove]);

    // Flatten products into variants with product information
    // Handle both product response format and variant response format
    const allVariants = useMemo(() => {
        if (!allProducts || allProducts.length === 0) return [];

        // Check if first item has a 'product' property (variant format from API)
        const isVariantFormat = allProducts[0]?.product !== undefined;

        if (isVariantFormat) {
            // Data is already in variant format with nested product
            return allProducts.map((variant) => ({
                ...variant,
                productTitle: variant.product?.title,
                productImage: variant.product?.image,
                productVendor: variant.product?.vendor,
                productId: variant.product?.id,
            }));
        } else {
            // Data is in product format with nested variants
            return allProducts.flatMap((product) =>
                (product.variants || []).map((variant) => ({
                    ...variant,
                    productTitle: product.title,
                    productImage: product.image,
                    productVendor: product.vendor,
                    productId: product.id,
                }))
            );
        }
    }, [allProducts]);

    // Filtered variants
    const filteredProducts = useMemo(() => {
        return allVariants.filter((variant) => {
            if (productNameFilter && !variant.productTitle?.toLowerCase().includes(productNameFilter.toLowerCase())) return false;
            if (skuFilter && !variant.sku?.toLowerCase().includes(skuFilter.toLowerCase())) return false;
            if (queryValueFilter) {
                const query = queryValueFilter.toLowerCase();
                const matchesTitle = variant.productTitle?.toLowerCase().includes(query);
                const matchesVariantTitle = variant.title?.toLowerCase().includes(query);
                const matchesSKU = variant.sku?.toLowerCase().includes(query);
                const matchesBarcode = variant.barcode?.toLowerCase().includes(query);
                const matchesVendor = variant.productVendor?.toLowerCase().includes(query);
                if (!matchesTitle && !matchesVariantTitle && !matchesSKU && !matchesBarcode && !matchesVendor) return false;
            }
            return true;
        });
    }, [allVariants, productNameFilter, skuFilter, queryValueFilter]);

    return (
        <Page fullWidth title="Create Count">
            <Layout>
                <Layout.Section>
                    <Card>
                        <div style={{ width: '100%', maxWidth: 500 }}>
                            <BlockStack gap="400">
                                <Text as="h2" variant="headingMd">
                                    Cycle Count Form
                                </Text>

                                <Form onSubmit={handleSubmit}>
                                    <FormLayout>
                                        {/* Store Selection */}
                                        <FormLayout.Group>
                                            <Combobox
                                                allowMultiple
                                                activator={
                                                    <Combobox.TextField
                                                        label="Store Selection"
                                                        value={storeSearchValue}
                                                        onChange={setStoreSearchValue}
                                                        placeholder="Select stores"
                                                        autoComplete="off"
                                                        verticalContent={storeVerticalContent}
                                                        error={showValidationErrors && !hasStores ? 'At least one store is required' : undefined}
                                                    />
                                                }
                                            >
                                                <Listbox onSelect={handleStoreSelect}>
                                                    <Listbox.Option key="ALL" value="ALL" selected={selectedStoreIds.length === availableStores.length}>
                                                        {selectedStoreIds.length === availableStores.length ? 'Deselect all stores' : 'Select all stores'}
                                                    </Listbox.Option>
                                                    {filteredStores.map((store) => (
                                                        <Listbox.Option key={store.id} value={store.id} selected={selectedStoreIds.includes(store.id)}>
                                                            {store.name}
                                                        </Listbox.Option>
                                                    ))}
                                                </Listbox>
                                            </Combobox>
                                        </FormLayout.Group>

                                        {/* Count Type */}
                                        <FormLayout.Group>
                                            <Combobox
                                                activator={
                                                    <Combobox.TextField
                                                        label="Count Type"
                                                        value={countType ? countTypeOptions.find((t) => t.value === countType)?.label || '' : ''}
                                                        placeholder="Select count type"
                                                        autoComplete="off"
                                                        error={showValidationErrors && !hasCountType ? 'Count type is required' : undefined}
                                                    />
                                                }
                                            >
                                                <Listbox
                                                    onSelect={(value) => {
                                                        setCountType(value);
                                                    }}
                                                >
                                                    {countTypeOptions.map((type) => (
                                                        <Listbox.Option key={type.value} value={type.value} selected={countType === type.value}>
                                                            {type.label}
                                                        </Listbox.Option>
                                                    ))}
                                                </Listbox>
                                            </Combobox>
                                        </FormLayout.Group>

                                        {/* Reason Code */}
                                        <FormLayout.Group>
                                            <Combobox
                                                activator={
                                                    <Combobox.TextField
                                                        label="Reason Code"
                                                        value={reasonCode ? filteredReasonCodes.find((r) => r.id === reasonCode)?.label || '' : ''}
                                                        placeholder={isLoadingReasonCodes ? 'Loading...' : countType ? 'Select reason code' : 'Select count type first'}
                                                        autoComplete="off"
                                                        disabled={isLoadingReasonCodes || !countType}
                                                        error={showValidationErrors && !hasReasonCode ? 'Reason code is required' : undefined}
                                                    />
                                                }
                                            >
                                                <Listbox
                                                    onSelect={(value) => {
                                                        setReasonCode(value);
                                                    }}
                                                >
                                                    {filteredReasonCodes.map((code, index) => (
                                                        <Listbox.Option key={code.value + '-' + index} value={code.id} selected={reasonCode === code.id}>
                                                            {code.label}
                                                        </Listbox.Option>
                                                    ))}
                                                </Listbox>
                                            </Combobox>
                                        </FormLayout.Group>

                                        {/* Description */}
                                        <FormLayout.Group>
                                            <TextField
                                                label="Description"
                                                value={description}
                                                onChange={setDescription}
                                                placeholder="Enter description"
                                                autoComplete="off"
                                                maxLength={30}
                                                showCharacterCount
                                                error={showValidationErrors && !hasDescription ? 'Description is required' : undefined}
                                            />
                                        </FormLayout.Group>
                                    </FormLayout>
                                </Form>
                            </BlockStack>
                        </div>
                        <BlockStack>
                            {/* Action Buttons */}
                            <Box paddingBlockStart="400">
                                <InlineStack align="end" gap="200">
                                    <Button onClick={handleCancel}>Cancel</Button>
                                    {canCreateCycleCount && (
                                        <Button variant="primary" onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting}>
                                            Create Cycle Count
                                        </Button>
                                    )}
                                </InlineStack>
                            </Box>
                        </BlockStack>
                    </Card>
                </Layout.Section>

                {/* Product Selection Rules */}
                {countType === 'partial' && (
                    <Layout.Section>
                        <Card>
                            <Box paddingBlock="400">
                                <Box paddingBlock="400">
                                    <Text variant="headingLg">
                                        Product Selection Rules
                                        <Box as="span" paddingInlineStart="200">
                                            <Text as="span" variant="bodyMd" tone="critical">
                                                *
                                            </Text>
                                        </Box>
                                    </Text>
                                </Box>
                                <MultiProductFilter storeType="cycleCount" />
                            </Box>
                        </Card>
                    </Layout.Section>
                )}

                {/* Included Product List */}
                {countType === 'partial' && restrictToEligibleProducts && allProducts.length > 0 && (
                    <Layout.Section>
                        <Card>
                            <Box paddingBlock="400">
                                <Box paddingBlock="400">
                                    <Text variant="headingLg">Included Product List</Text>
                                </Box>

                                <IndexFilters
                                    queryValue={queryValueFilter}
                                    queryPlaceholder="Search Products"
                                    onQueryChange={handleQueryValueChange}
                                    onQueryClear={handleQueryValueRemove}
                                    cancelAction={{
                                        onAction: onHandleCancel,
                                        disabled: false,
                                        loading: false,
                                    }}
                                    tabs={[]}
                                    selected={selected}
                                    onSelect={setSelected}
                                    filters={productFilters}
                                    appliedFilters={appliedFilters}
                                    onClearAll={handleFiltersClearAll}
                                    mode={filterMode}
                                    setMode={setFilterMode}
                                    autoFocusSearchField={false}
                                />
                                <IndexTable
                                    condensed={breakpoints.smDown}
                                    resourceName={{
                                        singular: 'variant',
                                        plural: 'variants',
                                    }}
                                    itemCount={filteredProducts.length}
                                    headings={[{ title: 'Product' }, { title: 'Variant' }, { title: 'SKU' }, { title: 'Barcode' }, { title: 'Vendor' }]}
                                    selectable={false}
                                >
                                    {filteredProducts.map((variant, index) => (
                                        <IndexTable.Row id={variant.id || index} key={variant.id || index} position={index}>
                                            <IndexTable.Cell>
                                                <Box paddingBlockEnd="200">
                                                    <InlineStack gap="400" align="start" blockAlign="center">
                                                        {renderImage(variant.productImage, variant.productTitle)}
                                                        <Box
                                                            style={{
                                                                maxWidth: 250,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                display: 'inline-block',
                                                                verticalAlign: 'middle',
                                                            }}
                                                        >
                                                            <Text variant="bodyMd" fontWeight="bold" as="span" title={variant.productTitle || 'N/A'}>
                                                                {variant.productTitle || 'N/A'}
                                                            </Text>
                                                        </Box>
                                                    </InlineStack>
                                                </Box>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <Text variant="bodyMd" as="span">
                                                    {variant.title || 'N/A'}
                                                </Text>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <Text variant="bodyMd" as="span">
                                                    {variant.sku || 'N/A'}
                                                </Text>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <Text variant="bodyMd" as="span">
                                                    {variant.barcode || 'N/A'}
                                                </Text>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <Text variant="bodyMd" as="span">
                                                    {variant.productVendor || 'N/A'}
                                                </Text>
                                            </IndexTable.Cell>
                                        </IndexTable.Row>
                                    ))}
                                </IndexTable>
                                {snapshotId && totalProductCount > itemsPerPage && (
                                    <Box padding="400">
                                        <Pagination
                                            hasPrevious={currentPage > 1}
                                            hasNext={currentPage * itemsPerPage < totalProductCount}
                                            onPrevious={() => handlePageNavigation(currentPage - 1)}
                                            onNext={() => handlePageNavigation(currentPage + 1)}
                                            label={`${(currentPage - 1) * itemsPerPage + 1} - ${Math.min(currentPage * itemsPerPage, totalProductCount)} of ${totalProductCount}`}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Card>
                    </Layout.Section>
                )}
            </Layout>
        </Page>
    );
};

export default CreateCycleCounts;
